import React, { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import * as yup from 'yup';

import FixAddressComponent from '@/components/FixAddress/FixAddressComponent';
import normalizeText from '@/components/FixAddress/normalizeText';
import { useUser } from '@/contexts/UserContext';
import states from '@/data/states-br.json';
import AddressService from '@/services/addressService';

const validationSchema = yup.object().shape({
  state: yup.object().required(),
  city: yup.object().required(),
  neighborhood: yup.object().required(),
});

const FixAddressContainer = ({ orderData, onChange }) => {
  const [cities, setCities] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const {
    address_validation: addressValidation,
    form: { address },
  } = orderData;
  const formik = useRef();
  const initialValues = {
    state: '',
    city: '',
    neighborhood: '',
  };
  const me = useUser();
  const addressService = new AddressService(me?.profile?.country);

  const fetchNeighborhoods = async cityId => {
    if (!cityId) return [];
    const data = await addressService.getNeighborhoodsByCity(cityId);
    const options = data.map(neighborhood => ({
      value: neighborhood.id,
      label: neighborhood.name,
    }));
    setNeighborhoods(options);
    return data;
  };

  const fetchCities = async stateId => {
    if (!stateId) return [];
    const data = await addressService.getCitiesByState(stateId);
    setCities(data.map(city => ({ value: city.id, label: city.name })));
    return data;
  };

  const handleStateChange = async (state, reloadCities = true) => {
    formik.current.setFieldValue('state', state || '');

    if (reloadCities) {
      formik.current.setFieldValue('city', '');
      formik.current.setFieldValue('neighborhood', '');
      setNeighborhoods([]);
      setCities([]);
      if (state) {
        const citiesData = await fetchCities(state.value);
        const originalCity = address?.city;
        if (citiesData && originalCity) {
          const normalizedOriginalCity = normalizeText(originalCity);
          const matchCity = citiesData.find(
            c => normalizeText(c.name) === normalizedOriginalCity
          );
          if (matchCity) {
            const cityOption = { value: matchCity.id, label: matchCity.name };
            formik.current.setFieldValue('city', cityOption);

            const neighborhoodsData = await fetchNeighborhoods(matchCity.id);
            const originalNeighborhood = address?.neighborhood;
            if (neighborhoodsData && originalNeighborhood) {
              const normalizedOriginalNeighborhood = normalizeText(originalNeighborhood);
              const matchNeighborhood = neighborhoodsData.find(
                n => normalizeText(n.name) === normalizedOriginalNeighborhood
              );
              if (matchNeighborhood) {
                formik.current.setFieldValue('neighborhood', {
                  value: matchNeighborhood.id,
                  label: matchNeighborhood.name,
                });
              } else {
                formik.current.setFieldValue('neighborhood', {
                  value: null,
                  label: normalizeText(originalNeighborhood),
                });
              }
            }
          }
        }
      }
    }
  };

  const handleCityChange = async city => {
    formik.current.setFieldValue('city', city || '');
    formik.current.setFieldValue('neighborhood', '');
    setNeighborhoods([]);
    if (city) {
      const neighborhoodsData = await fetchNeighborhoods(city.value);
      const originalNeighborhood = address?.neighborhood;
      if (neighborhoodsData && originalNeighborhood) {
        const normalizedOriginalNeighborhood = normalizeText(originalNeighborhood);
        const matchNeighborhood = neighborhoodsData.find(
          n => normalizeText(n.name) === normalizedOriginalNeighborhood
        );
        if (matchNeighborhood) {
          formik.current.setFieldValue('neighborhood', {
            value: matchNeighborhood.id,
            label: matchNeighborhood.name,
          });
        } else {
          formik.current.setFieldValue('neighborhood', {
            value: null,
            label: normalizeText(originalNeighborhood),
          });
        }
      }
    }
  };

  const handleNeighborhoodChange = async neighborhood => {
    formik.current.setFieldValue('neighborhood', neighborhood || '');
    if (!neighborhood) onChange(null);
  };

  const selectPrediction = ({ state, city, neighborhood }) => {
    formik.current.setFieldValue('state', {
      value: state.id,
      label: state.name,
    });
    formik.current.setFieldValue('city', { value: city.id, label: city.name });
    formik.current.setFieldValue('neighborhood', {
      value: neighborhood.id,
      label: neighborhood.name,
    });
    fetchCities(state.id);
    fetchNeighborhoods(city.id);
  };

  const handleSubmit = values => {
    onChange({
      state: {
        id: values.state?.value,
        name: values.state?.label,
      },
      city: {
        id: values.city?.value,
        name: values.city?.label,
      },
      neighborhood: {
        id: typeof values.neighborhood?.value === 'number' ? values.neighborhood?.value : null,
        name: normalizeText(values.neighborhood?.label),
      },
    });
  };

  useEffect(() => {
    const prefillAddress = async () => {
      const originalState = orderData?.form?.address?.state;
      const stateOption = states.find(state => state.name === originalState || state.label === originalState);
      if (!stateOption) return;

      formik.current.setFieldValue('state', { value: stateOption.id, label: stateOption.name });

      const citiesData = await fetchCities(stateOption.id);
      const originalCity = orderData?.form?.address?.city;
      if (!originalCity) return;

      const normalizedOriginalCity = normalizeText(originalCity);
      const cityOption = citiesData.find(city => normalizeText(city.name) === normalizedOriginalCity);
      if (!cityOption) return;

      const cityVal = { value: cityOption.id, label: cityOption.name };
      formik.current.setFieldValue('city', cityVal);

      const neighborhoodsData = await fetchNeighborhoods(cityOption.id);
      const originalNeighborhood = orderData?.form?.address?.neighborhood;
      if (!originalNeighborhood) return;

      const normalizedOriginalNeighborhood = normalizeText(originalNeighborhood);
      const neighborhoodOption = neighborhoodsData.find(
        n => normalizeText(n.name) === normalizedOriginalNeighborhood
      );
      if (neighborhoodOption) {
        formik.current.setFieldValue('neighborhood', {
          value: neighborhoodOption.id,
          label: neighborhoodOption.name,
        });
      } else {
        formik.current.setFieldValue('neighborhood', {
          value: null,
          label: normalizeText(originalNeighborhood),
        });
      }
    };

    prefillAddress();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FixAddressComponent
      initialValues={initialValues}
      address={address}
      states={states.map(state => ({ value: state.id, label: state.name }))}
      cities={cities}
      neighborhoods={neighborhoods}
      predictions={orderData?.address_validation?.predictions}
      addressValidation={addressValidation || undefined}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      onCityChange={handleCityChange}
      onNeighborhoodChange={handleNeighborhoodChange}
      onStateChange={handleStateChange}
      selectPrediction={selectPrediction}
      formikRef={formik}
    />
  );
};

FixAddressContainer.propTypes = {
  orderData: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default FixAddressContainer;
